/** Hall / share timestamps: /video/{dbId}?t=seconds (YouTube-style t= also parsed). */

const VIDEO_PATH_RE = /^(?:\/legacy)?\/video\/([^/?#]+)\/?$/i;

export function formatVideoTime(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function parseTimeParam(raw) {
  if (raw == null || raw === '') return 0;
  const str = String(raw).trim();
  if (!str) return 0;

  if (/^\d+$/.test(str)) {
    const n = parseInt(str, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  const clock = str.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (clock) {
    if (clock[3] != null) {
      const h = parseInt(clock[1], 10);
      const m = parseInt(clock[2], 10);
      const sec = parseInt(clock[3], 10);
      if (m > 59 || sec > 59) return 0;
      return h * 3600 + m * 60 + sec;
    }
    const m = parseInt(clock[1], 10);
    const sec = parseInt(clock[2], 10);
    if (sec > 59) return 0;
    return m * 60 + sec;
  }

  const youTube = str.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (youTube && (youTube[1] || youTube[2] || youTube[3])) {
    const h = parseInt(youTube[1] || '0', 10);
    const m = parseInt(youTube[2] || '0', 10);
    const sec = parseInt(youTube[3] || '0', 10);
    return h * 3600 + m * 60 + sec;
  }

  return 0;
}

export function parseVideoMomentHref(href) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'https://bigplays.gg');
  } catch {
    return null;
  }
  const pathMatch = url.pathname.replace(/\/+$/, '').match(VIDEO_PATH_RE);
  if (!pathMatch) return null;
  let id;
  try {
    id = decodeURIComponent(pathMatch[1]);
  } catch {
    id = pathMatch[1];
  }
  if (!id) return null;
  const t = parseTimeParam(url.searchParams.get('t'));
  return { videoId: id, t };
}

export function buildVideoMomentPath(dbVideoId, seconds, { forceT = false } = {}) {
  const id = encodeURIComponent(String(dbVideoId));
  const t = Math.max(0, Math.floor(Number(seconds) || 0));
  if (t > 0 || forceT) return `/video/${id}?t=${t}`;
  return `/video/${id}`;
}

export function buildVideoMomentUrl(dbVideoId, seconds, options) {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://bigplays.gg';
  return `${origin}${buildVideoMomentPath(dbVideoId, seconds, options)}`;
}

export function formatVideoMomentLabel(title, totalSeconds) {
  const name = String(title || '').trim();
  const t = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const time = t > 0 ? formatVideoTime(t) : '';
  if (name && time) return `${name} ${time}`;
  if (name) return name;
  if (time) return time;
  return 'Video';
}

const titleCache = Object.create(null);
const titleInflight = Object.create(null);

export function rememberVideoTitle(id, title) {
  if (id == null) return;
  const key = String(id);
  const name = String(title || '').trim();
  if (name) titleCache[key] = name;
}

export function getCachedVideoTitle(id) {
  if (id == null) return '';
  return titleCache[String(id)] || '';
}

export function fetchVideoTitle(id) {
  if (id == null || id === '') return Promise.resolve('');
  const key = String(id);
  if (Object.prototype.hasOwnProperty.call(titleCache, key)) {
    return Promise.resolve(titleCache[key]);
  }
  if (titleInflight[key]) return titleInflight[key];
  titleInflight[key] = fetch(`/api/videos/${encodeURIComponent(key)}`, {
    credentials: 'include'
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const name = String(data?.title || '').trim();
      titleCache[key] = name;
      if (data?.videoId != null) rememberVideoTitle(data.videoId, name);
      return name;
    })
    .catch(() => {
      titleCache[key] = '';
      return '';
    })
    .finally(() => {
      delete titleInflight[key];
    });
  return titleInflight[key];
}
